"use client";

import React, { useState, useRef } from "react";
import axios from "axios";
import ReactPhoneInput from "react-phone-input-2";
import ReCAPTCHA from "react-google-recaptcha";
import { Toast, ToastContainer } from "react-bootstrap";
import "react-phone-input-2/lib/style.css";
import "./ContactForm.css";

const ContactForm = () => {
  const [loading, setLoading] = useState(false);

  const [fullname, setFullname] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState("");

  const [errors, setErrors] = useState<any>({});
  const [submitted, setSubmitted] = useState(false);

  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState<"success" | "danger">("success");
  const [showToast, setShowToast] = useState(false);

  const recaptchaRef = useRef<any>(null);

  const showMsg = (msg: string, type: "success" | "danger") => {
    setToastMessage(msg);
    setToastType(type);
    setShowToast(true);
  };

  /* ----------------------------------------------------------
      VALIDATION
  -----------------------------------------------------------*/
  const validateForm = () => {
    const newErrors: any = {};

    // Full Name
    if (!fullname) newErrors.fullname = "Full Name is required";
    else if (!/^[a-zA-Z0-9 ]+$/.test(fullname))
      newErrors.fullname = "Full Name must contain letters and numbers only";

    // Email
    if (!email) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(email))
      newErrors.email = "Enter a valid email address";

    // Phone (E.164: 10–15 digits)
    const numericPhone = phone.replace(/\D/g, "");
    if (!numericPhone)
      newErrors.phone = "Phone number is required";
    else if (numericPhone.length < 10)
      newErrors.phone = "Phone number must be at least 10 digits";
    else if (numericPhone.length > 15)
      newErrors.phone = "Phone number must not exceed 15 digits";

    // Subject
    if (!subject) newErrors.subject = "Subject is required";

    // Message
    if (!message) newErrors.message = "Message is required";
    else if (message.trim().split(/\s+/).length < 5)
      newErrors.message = "Message must be at least 5 words";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ----------------------------------------------------------
      SUBMIT HANDLER
  -----------------------------------------------------------*/
  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setSubmitted(true);

    if (!recaptchaToken) {
      showMsg("Please complete the reCAPTCHA verification.", "danger");
      return;
    }

    if (!validateForm()) return;

    setLoading(true);

    try {
      const res = await axios.post("/api/contact", {
        fullname,
        email,
        phone,
        subject,
        message,
        recaptcha_token: recaptchaToken,
      });

      if (res.data.success) {
        showMsg(res.data.message, "success");

        setFullname("");
        setEmail("");
        setPhone("");
        setSubject("");
        setMessage("");
        setRecaptchaToken("");
        setErrors({});
        setSubmitted(false);

        recaptchaRef.current?.reset();
      } else {
        showMsg(res.data.message || "Submission failed.", "danger");
        recaptchaRef.current?.reset();
      }
    } catch (err: any) {
      showMsg(
        err.response?.data?.message || "Error submitting message.",
        "danger"
      );
      recaptchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="container contact-wrapper" id="ContactForm">
        <div className="contact-box shadow-sm">
          <h4 className="contact-title">Get In Touch</h4>

          <form className="row" onSubmit={handleSubmit} noValidate>

            {/* Full Name */}
            <div className="col-lg-6 mb-3">
              <input
                type="text"
                placeholder="Full Name"
                className="form-control contfield"
                value={fullname}
                required
                onChange={(e) => {
                  setFullname(e.target.value);
                  if (submitted) setErrors({ ...errors, fullname: "" });
                }}
              />
              {submitted && errors.fullname && (
                <div className="text-danger">{errors.fullname}</div>
              )}
            </div>

            {/* Email */}
            <div className="col-lg-6 mb-3">
              <input
                type="email"
                placeholder="Email Address"
                className="form-control contfield"
                value={email}
                required
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (submitted) setErrors({ ...errors, email: "" });
                }}
              />
              {submitted && errors.email && (
                <div className="text-danger">{errors.email}</div>
              )}
            </div>

            {/* Phone */}
            <div className="col-lg-6 mb-3">
              <ReactPhoneInput
                inputProps={{
                  name: "phone",
                  required: true,
                  className: "form-control contfield phone-input",
                }}
                country={"us"}
                value={phone}
                onChange={(v) => {
                  const digits = v.replace(/\D/g, "");
                  if (digits.length <= 15) {
                    setPhone(v);
                    if (submitted) setErrors({ ...errors, phone: "" });
                  }
                }}
              />
              {submitted && errors.phone && (
                <div className="text-danger">{errors.phone}</div>
              )}
            </div>

            {/* Subject */}
            <div className="col-lg-6 mb-3">
              <input
                type="text"
                placeholder="Subject"
                className="form-control contfield"
                value={subject}
                required
                onChange={(e) => {
                  setSubject(e.target.value);
                  if (submitted) setErrors({ ...errors, subject: "" });
                }}
              />
              {submitted && errors.subject && (
                <div className="text-danger">{errors.subject}</div>
              )}
            </div>

            {/* Message */}
            <div className="col-lg-12 mb-3">
              <textarea
                placeholder="Enter your message"
                rows={4}
                className="form-control contfield textarea-field"
                value={message}
                required
                onChange={(e) => {
                  setMessage(e.target.value);
                  if (submitted) setErrors({ ...errors, message: "" });
                }}
              />
              {submitted && errors.message && (
                <div className="text-danger">{errors.message}</div>
              )}
            </div>

            {/* ReCAPTCHA */}
            <div className="col-lg-12 mb-3">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ""}
                onChange={(token) => setRecaptchaToken(token || "")}
              />
            </div>

            {/* Submit */}
            <div className="col-lg-12 mt-2">
              <button
                type="submit"
                disabled={loading || !recaptchaToken}
                className="w-100 btn btn-lg fw-semibold submit-btn"
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Sending...
                  </>
                ) : (
                  "Submit"
                )}
              </button>
            </div>

          </form>
        </div>
      </section>

      {/* Toast */}
      <ToastContainer position="top-end" className="p-3">
        <Toast
          bg={toastType}
          show={showToast}
          autohide
          delay={3500}
          onClose={() => setShowToast(false)}
        >
          <Toast.Body className="text-white">
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default ContactForm;
